import { observable, action, runInAction, decorate } from "mobx";
import {
  deletePaymentEmail,
  deleteSenderEmailConfig,
  getNotificationSettings,
  saveSenderEmailConfig,
  savePaymentEmailConfig,
  saveOptoutEmailConfig,
  deleteOptOutEmail,
  deleteAllInbound,
  updateInbound,
  addEmail,
  deleteEmail,
  getEmailList,
  getAlertConfiguration,
  saveAlertConfiguration
} from "service/alertSettingsService";

class NotificationSettingStore {
  settings = null;
  total = 0;
  pageSize = 10;
  current = 1;
  search = "";
  sortField = "createdAt";
  sortOrder = "descend";
  totalPages = 0;
  list = [];
  data = [];
  payments = [];
  optout = [];
  fullEmails = [];
  inboundStatus = false;

  changeSorting = fieldObj => {
    runInAction(() => {
      const { sortField, sortOrder } = fieldObj;
      this.sortField = sortField;
      this.sortOrder = sortOrder;
    });
  };

  setSearch = search => {
    runInAction(() => {
      this.search = search;
      this.current = 1;
    });
  };

  setPage = current => {
    runInAction(() => {
      this.current = current;
    });
  };

  setPageSize = pageSize => {
    runInAction(() => {
      this.pageSize = pageSize;
      this.current = 1;
    });
  };

  getEmailList = async (args = {}) => {
    const params = {
      limit: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      ...(this.search && { search: this.search }),
      ...(this.current && { page: this.current }),
      ...args
    };

    try {
      const response = await getEmailList(params);

      runInAction(() => {
        if (response.status) {
          const { items, total, current, limit: pageSize, totalPages } = response?.data;
          this.list = items || [];
          this.pageSize = pageSize;
          this.current = current;
          this.total = total;
          this.totalPages = totalPages;
        }
      });

      return response;
    } catch (error) {
      console.error("Failed to fetch emails:", error);
      return { status: false, error };
    }
  };

  addEmail = async payload => {
    try {
      const response = await addEmail(payload);
      if (response.status) {
        await this.getEmailList();
      }
      return response;
    } catch (error) {
      console.error("Failed to add email:", error);
      return { status: false, error };
    }
  };

  fetchAllEmails = async () => {
    try {
      const response = await getEmailList();
      runInAction(() => {
        this.fullEmails = response.status ? response.data : [];
      });
    } catch (error) {
      console.error("Failed to fetch all emails:", error);
      runInAction(() => {
        this.fullEmails = [];
      });
    }
  };

  getAlertConfiguration = async () => {
    try {
      this.loading = true;
      const response = await getAlertConfiguration();
      runInAction(() => {
        this.settings = response.data;
      });
      return response;
    } catch (error) {
      console.error("Failed to fetch alert configuration:", error);
      return { status: false, error };
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  saveAlertConfiguration = async payload => {
    this.loading = true;
    try {
      const response = await saveAlertConfiguration(payload);
      runInAction(() => {
        this.settings = response.data;
      });
      return response;
    } catch (error) {
      console.error("Failed to save alert configuration:", error);
      return { status: false, error };
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  deleteEmail = async emailId => {
    try {
      const response = await deleteEmail(emailId);
      runInAction(() => {
        if (response.status) {
          this.list = this.list.filter(email => email._id !== emailId);
        }
      });
      return response;
    } catch (error) {
      console.error("Failed to delete email:", error);
      return { status: false, error };
    }
  };

  fetchSettings = async () => {
    try {
      const response = await getNotificationSettings();
      runInAction(() => {
        const { inbound = [], payment = [], optOut = [], inboundStatus } = response.data;

        this.data = inbound.map((item, index) => ({
          key: item.senderId,
          senderId: item.senderId,
          emails: item.emails
        }));

        this.payments = payment;
        this.optout = optOut;
        this.inboundStatus = inboundStatus;
      });
    } catch (error) {
      console.error("Failed to fetch notification settings", error);
    }
  };

  addorUpdateSender = async (senderId, emails) => {
    try {
      const payload = { senderId, emails };
      const res = await saveSenderEmailConfig(payload);

      if (res.status) {
        await this.fetchSettings();
      }

      return res;
    } catch (error) {
      console.error("Failed to save sender", error);
      return { status: false, error };
    }
  };

  deleteSender = async senderId => {
    try {
      const res = await deleteSenderEmailConfig(senderId);

      if (res.status) {
        await this.fetchSettings();
      }

      return res;
    } catch (error) {
      console.error("Failed to delete sender config:", error);
      return { status: false, error };
    }
  };

  addEmailsToPayments = async (emails = []) => {
    try {
      const existing = this.payments || [];

      const updatedEmails = Array.from(new Set([...existing, ...emails]));

      const res = await savePaymentEmailConfig(updatedEmails);

      if (res?.status) {
        runInAction(() => {
          this.payments = updatedEmails;
        });
      }

      return res;
    } catch (error) {
      console.error("Failed to add emails to payment notifications:", error);
      return { status: false, error };
    }
  };

  addEmailsToOptout = async email => {
    try {
      const newEmails = Array.isArray(email) ? email.flat() : [email];

      const updatedEmails = Array.from(new Set([...this.optout, ...newEmails]));

      const res = await saveOptoutEmailConfig(updatedEmails);

      if (res?.status) {
        runInAction(() => {
          this.optout = updatedEmails;
        });
      }

      return res;
    } catch (error) {
      console.error("Failed to add email to opt-out notifications:", error);
      return { status: false, error };
    }
  };

  deletePaymentNotificationEmail = async email => {
    try {
      const res = await deletePaymentEmail(email);

      if (res?.status) {
        runInAction(() => {
          this.payments = this.payments.filter(e => e !== email);
        });
      }

      return res;
    } catch (error) {
      console.error("Failed to delete payment notification email:", error);
      return { status: false, error };
    }
  };

  deleteOptOutNotificationEmail = async email => {
    try {
      const res = await deleteOptOutEmail(email);

      if (res?.status) {
        runInAction(() => {
          this.optout = this.optout.filter(e => e !== email);
        });
      }

      return res;
    } catch (error) {
      console.error("Failed to delete opt-out notification email:", error);
      return { status: false, error };
    }
  };

  deleteAllInboundConfiguration = async inboundStatus => {
    try {
      const res = await deleteAllInbound(inboundStatus);

      if (res?.status) {
        runInAction(() => {
          this.inboundStatus = inboundStatus;
        });
      }

      return res;
    } catch (error) {
      console.error("Failed to delete all inbound status");
      return { status: false, error };
    }
  };

  updateInbound = async (senderId, emails) => {
    try {
      const res = await updateInbound(senderId, emails);
      if (res?.status) {
        await this.fetchSettings();
      }
      return res;
    } catch (err) {
      console.error("Failed to update inbound notification:", err);
      return { status: false, error: err };
    }
  };
}

decorate(NotificationSettingStore, {
  list: observable,
  total: observable,
  pageSize: observable,
  current: observable,
  search: observable,
  settings: observable,
  inboundStatus: observable,
  fullEmails: observable,
  data: observable,
  payments: observable,
  optout: observable,
  getAlertConfiguration: action,
  deleteEmail: action,
  fetchSettings: action,
  addorUpdateSender: action,
  deleteSender: action,
  addEmailsToPayments: action,
  addEmailsToOptout: action,
  deleteAllInbound: action,
  updateInbound: action,
  deleteAllInboundConfiguration: action,
  getEmailList: action
});

export default new NotificationSettingStore();
